import React, { useState, useEffect } from 'react';
import { jobSeekersAPI, unwrapList } from '../services/api';
import Pagination, { paginateItems } from './Pagination';

function JobSeekers() {
  const [jobSeekers, setJobSeekers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // State for search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [experienceFilter, setExperienceFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSeeker, setSelectedSeeker] = useState(null);

  // Fetch job seekers data when component mounts
  useEffect(() => {
    fetchJobSeekers();
  }, []);

  const fetchJobSeekers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await jobSeekersAPI.getAllJobSeekers();
      setJobSeekers(unwrapList(response.data));
    } catch (err) {
      setError('Failed to fetch job seekers');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter job seekers based on search term and filters
  const filteredSeekers = jobSeekers.filter(seeker => {
    const matchesSearch = 
      seeker.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
      (seeker.expertise_areas_data && seeker.expertise_areas_data.some(area => 
        area.name && area.name.toLowerCase().includes(searchTerm.toLowerCase())
      )) ||
      (seeker.place && seeker.place.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (seeker.unique_id && seeker.unique_id.toLowerCase().includes(searchTerm.toLowerCase())); // Add unique_id search
    
    const matchesStatus = statusFilter === 'All' || 
      (statusFilter === 'Open' && seeker.work_status === 'OPEN') || 
      (statusFilter === 'Working' && seeker.work_status === 'WORKING');
    
    const matchesExperience = experienceFilter === 'All' || 
      (experienceFilter === '0-2' && parseFloat(seeker.years_of_experience) >= 0 && parseFloat(seeker.years_of_experience) <= 2) ||
      (experienceFilter === '3-5' && parseFloat(seeker.years_of_experience) > 2 && parseFloat(seeker.years_of_experience) <= 5) ||
      (experienceFilter === '6-8' && parseFloat(seeker.years_of_experience) > 5 && parseFloat(seeker.years_of_experience) <= 8) ||
      (experienceFilter === '8+' && parseFloat(seeker.years_of_experience) > 8);
    
    return matchesSearch && matchesStatus && matchesExperience;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, experienceFilter]);

  const { pageItems, total, currentPage: safePage } = paginateItems(filteredSeekers, currentPage);

  // Handle seeker selection for details view
  const handleViewDetails = (seeker) => {
    setSelectedSeeker(seeker);
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedSeeker(null);
  };

  // Get work status display text
  const getWorkStatusText = (status) => {
    switch(status) {
      case 'OPEN': return 'Open to Work';
      case 'WORKING': return 'Currently Working';
      default: return status;
    }
  };

  // Get work status color class
  const getWorkStatusColorClass = (status) => {
    switch(status) {
      case 'OPEN': return 'bg-green-100 text-green-800';
      case 'WORKING': return 'bg-blue-100 text-blue-800';
      case 'NOT_OPEN': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> {error}</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">Job Seekers</h1>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              id="search"
              className="w-full p-2 border rounded-md"
              placeholder="Search by name, expertise, location, or ID"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">Work Status</label>
            <select
              id="status"
              className="w-full p-2 border rounded-md"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="All">All Statuses</option>
              <option value="Open">Open to Work</option>
              <option value="Working">Currently Working</option>
            </select>
          </div>
          <div>
            <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-1">Experience</label>
            <select
              id="experience"
              className="w-full p-2 border rounded-md"
              value={experienceFilter}
              onChange={(e) => setExperienceFilter(e.target.value)}
            >
              <option value="All">All Experience</option>
              <option value="0-2">0-2 years</option>
              <option value="3-5">3-5 years</option>
              <option value="6-8">6-8 years</option>
              <option value="8+">8+ years</option>
            </select>
          </div>
        </div>
      </div>

      {/* Job Seekers Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job Seeker</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unique ID</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Experience</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pageItems.length > 0 ? (
                pageItems.map((seeker) => (
                  <tr key={seeker.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {seeker.profile_picture ? (
                            <img 
                              src={seeker.profile_picture} 
                              alt={seeker.name} 
                              className="h-10 w-10 rounded-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(seeker.name)}&background=6366F1&color=fff`;
                              }}
                            />
                          ) : (
                            <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                              <span className="text-primary-600 font-medium">{seeker.name.charAt(0)}</span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{seeker.name}</div>
                          <div className="text-sm text-gray-500">
                            {seeker.expertise_areas_data && seeker.expertise_areas_data.length > 0 
                              ? seeker.expertise_areas_data.map(area => area.name).join(', ') 
                              : 'No expertise listed'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 bg-gray-100 rounded-md font-mono text-sm">
                        {seeker.unique_id || 'Not assigned'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>{seeker.email}</div>
                      <div>{seeker.contact_no}</div>
                      <div>{seeker.place || 'Location not specified'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {seeker.years_of_experience} years
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        getWorkStatusColorClass(seeker.work_status)
                      }`}>
                        {getWorkStatusText(seeker.work_status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {seeker.overall_rating > 0 ? (
                        <div className="flex items-center">
                          <span className="text-yellow-500 mr-1">★</span>
                          <span>{seeker.overall_rating.toFixed(1)}</span>
                        </div>
                      ) : 'Not rated'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button 
                        onClick={() => handleViewDetails(seeker)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    No job seekers found matching your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination page={safePage} total={total} onPageChange={setCurrentPage} />

      {/* Details Modal */}
      {isModalOpen && selectedSeeker && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={closeModal}></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl leading-6 font-bold text-gray-900">
                        Job Seeker Profile
                      </h3>
                      <div className="flex items-center">
                        <span className="text-sm font-medium mr-2">Overall Rating:</span>
                        <div className="flex items-center">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <svg 
                              key={star} 
                              className={`w-5 h-5 ${star <= Math.round(selectedSeeker.overall_rating || 0) ? 'text-yellow-400' : 'text-gray-300'}`} 
                              fill="currentColor" 
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          ))}
                          <span className="ml-1 text-sm text-gray-600">
                            ({selectedSeeker.overall_rating > 0 ? selectedSeeker.overall_rating.toFixed(1) : 'N/A'})
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg mb-6">
                      <div className="flex flex-col md:flex-row">
                        <div className="md:w-1/4 flex justify-center mb-4 md:mb-0">
                          {selectedSeeker.profile_picture ? (
                            <img 
                              src={selectedSeeker.profile_picture} 
                              alt={selectedSeeker.name} 
                              className="h-32 w-32 rounded-full object-cover border-4 border-white shadow"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedSeeker.name)}&size=128&background=6366F1&color=fff`;
                              }}
                            />
                          ) : (
                            <div className="h-32 w-32 rounded-full bg-primary-100 flex items-center justify-center border-4 border-white shadow">
                              <span className="text-primary-600 text-4xl font-bold">{selectedSeeker.name.charAt(0)}</span>
                            </div>
                          )}
                        </div>
                        <div className="md:w-3/4 md:pl-6">
                          <h2 className="text-2xl font-bold text-gray-900 mb-1">{selectedSeeker.name}</h2>
                          <div className="text-sm text-gray-500 mb-2">
                            {selectedSeeker.expertise_areas_data && selectedSeeker.expertise_areas_data.length > 0 
                              ? selectedSeeker.expertise_areas_data.map(area => area.name).join(' • ') 
                              : 'No expertise listed'}
                          </div>
                          
                          {/* Add unique ID display */}
                          <div className="mb-3">
                            <span className="inline-block px-2 py-1 bg-gray-100 rounded-md font-mono text-sm">
                              {selectedSeeker.unique_id || 'ID not assigned'}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-4 mt-4">
                            <div className="flex items-center">
                              <svg className="h-5 w-5 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              <span>{selectedSeeker.email}</span>
                            </div>
                            <div className="flex items-center">
                              <svg className="h-5 w-5 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                              <span>{selectedSeeker.contact_no}</span>
                            </div>
                            <div className="flex items-center">
                              <svg className="h-5 w-5 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              <span>{selectedSeeker.place || 'Location not specified'}</span>
                            </div>
                            <div className="flex items-center">
                              <svg className="h-5 w-5 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              <span>{selectedSeeker.years_of_experience} years experience</span>
                            </div>
                            <div className="flex items-center">
                              <svg className="h-5 w-5 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              <span>{selectedSeeker.age ? `${selectedSeeker.age} years old` : 'Age not specified'}</span>
                            </div>
                            {selectedSeeker.resume && (
                              <div className="flex items-center">
                                <svg className="h-5 w-5 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <a 
                                  href={selectedSeeker.resume} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="text-primary-600 hover:text-primary-800"
                                >
                                  View Resume
                                </a>
                              </div>
                            )}
                            {selectedSeeker.social_link && (
                              <div className="flex items-center">
                                <svg className="h-5 w-5 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                </svg>
                                <a href={selectedSeeker.social_link.startsWith('http') ? selectedSeeker.social_link : `https://${selectedSeeker.social_link}`} 
                                   target="_blank" 
                                   rel="noopener noreferrer"
                                   className="text-blue-600 hover:underline">
                                  Social Profile
                                </a>
                              </div>
                            )}
                            <div className="flex items-center">
                              <svg className="h-5 w-5 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                              </svg>
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                selectedSeeker.work_status === 'OPEN' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {selectedSeeker.work_status === 'OPEN' ? 'Open to Work' : 'Not Open to Work'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {selectedSeeker.about && (
                      <div className="mb-6">
                        <h4 className="text-lg font-semibold text-gray-800 mb-2">About</h4>
                        <p className="text-gray-600">{selectedSeeker.about}</p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Work History Section */}
                      {selectedSeeker.work_history.length > 0 && (
                        <div className="bg-white rounded-lg border border-gray-200 p-4">
                          <h4 className="text-lg font-semibold text-gray-800 mb-3">Work History</h4>
                          <div className="space-y-4">
                            {selectedSeeker.work_history.map((work, index) => (
                              <div key={index} className="border-l-4 border-primary-500 pl-4 py-1">
                                <div className="font-medium text-gray-900">{work.job_title}</div>
                                <div className="text-sm text-gray-600">{work.salon_name}</div>
                                <div className="text-sm text-gray-500">
                                  {work.start_date} to {work.end_date || 'Present'}
                                </div>
                                <div className="text-sm text-gray-500 mt-1">
                                  Salary: ₹{parseFloat(work.current_salary).toLocaleString()}
                                </div>
                                <div className="text-sm text-gray-500">
                                  Status: <span className={`font-medium ${work.status === 'CURRENT' ? 'text-green-600' : 'text-gray-600'}`}>
                                    {work.status === 'CURRENT' ? 'Current' : 'Previous'}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Performance Metrics Section */}
                      {selectedSeeker.performance_metrics.length > 0 && (
                        <div className="bg-white rounded-lg border border-gray-200 p-4">
                          <h4 className="text-lg font-semibold text-gray-800 mb-3">Performance Metrics</h4>
                          <div className="space-y-6">
                            {selectedSeeker.performance_metrics.map((metric, index) => (
                              <div key={index} className="border-b border-gray-200 pb-4 last:border-b-0 last:pb-0">
                                <div className="font-medium text-gray-900">{metric.job_title}</div>
                                <div className="text-sm text-gray-600">{metric.salon_name}</div>
                                <div className="text-sm text-gray-500 mb-3">{metric.period}</div>
                                
                                <div className="space-y-2">
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium text-gray-600">Punctuality</span>
                                    <div className="flex">
                                      {[1, 2, 3, 4, 5].map((star) => (
                                        <svg 
                                          key={star} 
                                          className={`w-4 h-4 ${star <= metric.ratings.punctuality ? 'text-yellow-400' : 'text-gray-300'}`} 
                                          fill="currentColor" 
                                          viewBox="0 0 20 20"
                                        >
                                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                        </svg>
                                      ))}
                                    </div>
                                  </div>
                                  
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium text-gray-600">Communication</span>
                                    <div className="flex">
                                      {[1, 2, 3, 4, 5].map((star) => (
                                        <svg 
                                          key={star} 
                                          className={`w-4 h-4 ${star <= metric.ratings.communication ? 'text-yellow-400' : 'text-gray-300'}`} 
                                          fill="currentColor" 
                                          viewBox="0 0 20 20"
                                        >
                                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                        </svg>
                                      ))}
                                    </div>
                                  </div>
                                  
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium text-gray-600">Working Skill</span>
                                    <div className="flex">
                                      {[1, 2, 3, 4, 5].map((star) => (
                                        <svg 
                                          key={star} 
                                          className={`w-4 h-4 ${star <= metric.ratings.working_skill ? 'text-yellow-400' : 'text-gray-300'}`} 
                                          fill="currentColor" 
                                          viewBox="0 0 20 20"
                                        >
                                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                        </svg>
                                      ))}
                                    </div>
                                  </div>
                                  
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium text-gray-600">Behavior</span>
                                    <div className="flex">
                                      {[1, 2, 3, 4, 5].map((star) => (
                                        <svg 
                                          key={star} 
                                          className={`w-4 h-4 ${star <= metric.ratings.behavior ? 'text-yellow-400' : 'text-gray-300'}`} 
                                          fill="currentColor" 
                                          viewBox="0 0 20 20"
                                        >
                                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                        </svg>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                                
                                {metric.remarks && (
                                  <div className="mt-2 text-sm text-gray-600">
                                    {metric.remarks}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default JobSeekers;















